/**
 * PhotoPicker, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/PhotoPickerComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { reservedFieldNameErrors } from '../fieldName';

export const PhotoPickerCore = {
  type: 'PhotoPicker',
  name: 'Photo Picker',
  icon: '📷',
  description: 'Let the user upload one or more photos.',
  category: 'input',
  // Meta allows one per screen. This said null until 2026-09-24, so the builder would happily
  // stack two and let Meta refuse the publish. The MIXED case, a PhotoPicker beside a
  // DocumentPicker, which Meta also refuses, cannot be expressed here and lives in
  // flowValidation, which can see the whole screen.
  maxPerScreen: 1,
  getDefaultProperties() {
    return {
      name: 'photo_picker',
      label: 'Upload photos',
      'photo-source': 'camera_gallery',
      'max-file-size': '',
      'min-uploaded-photos': '',
      'max-uploaded-photos': '',
      required: false,
      enabled: true,
      visible: true,
      'helper-text': '',
      'error-message': '',
    };
  },
  getPropertySchema() {
    return {
      name: {
        type: 'string',
        label: 'Field Name',
        required: true,
      },
      label: {
        type: 'string',
        label: 'Label',
        required: true,
      },
      'photo-source': {
        type: 'select',
        label: 'Photo Source',
        required: false,
        options: ['camera_gallery', 'camera', 'gallery'],
      },
      'max-file-size': {
        type: 'number',
        label: 'Max File Size (bytes)',
        required: false,
      },
      'min-uploaded-photos': {
        type: 'number',
        label: 'Min Photos',
        required: false,
      },
      'max-uploaded-photos': {
        type: 'number',
        label: 'Max Photos',
        required: false,
      },
      required: {
        type: 'boolean',
        label: 'Required',
        required: false,
      },
      enabled: {
        type: 'boolean',
        label: 'Enabled',
        required: false,
      },
      visible: {
        type: 'boolean',
        label: 'Visible',
        required: false,
      },
      'helper-text': {
        type: 'string',
        label: 'Helper Text',
        required: false,
      },
      'error-message': {
        type: 'string',
        label: 'Error Message',
        required: false,
      },
    };
  },
  validateProperties(properties) {
    var errors = [];
    var warnings = [];

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }

    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore');
    }

    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Label is required');
    }

    if (properties.label && properties.label.length > 80) {
      errors.push('Label must be 80 characters or less');
    }

    if (properties['min-uploaded-photos'] && properties['max-uploaded-photos']) {
      var minPhotos = parseInt(properties['min-uploaded-photos'], 10);
      var maxPhotos = parseInt(properties['max-uploaded-photos'], 10);
      if (!isNaN(minPhotos) && !isNaN(maxPhotos) && minPhotos > maxPhotos) {
        errors.push('Min photos cannot be greater than max photos');
      }
    }

    if (properties['max-file-size']) {
      var maxSize = parseInt(properties['max-file-size'], 10);
      if (!isNaN(maxSize) && maxSize < 1024) {
        warnings.push('Max file size is very small (less than 1KB)');
      }
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    var result = {
      type: 'PhotoPicker',
      name: properties.name,
      label: properties.label,
    };

    if (properties['photo-source'] && properties['photo-source'] !== 'camera_gallery') {
      result['photo-source'] = properties['photo-source'];
    }

    // Note: max-file-size, required, helper-text, and error-message are NOT allowed
    // for PhotoPicker in WhatsApp Flow JSON schema. The size cap is spelled
    // `max-file-size-kb` there, which is a different property from our `max-file-size`.

    if (properties['min-uploaded-photos'] !== '' && properties['min-uploaded-photos'] !== undefined) {
      var minPhotos = parseInt(properties['min-uploaded-photos'], 10);
      if (!isNaN(minPhotos)) {
        result['min-uploaded-photos'] = minPhotos;
      }
    }

    // Meta REJECTS a published flow whose complete-action payload can carry more than 10
    // photos or more than 102400 KB of them, and its own defaults when these are unset are 30
    // photos at 25600 KB each, which breaks both limits at once. So every PhotoPicker is
    // capped here rather than left to whoever built it: the author cannot be expected to know
    // a rule whose default value violates it, and a generated flow has no author to ask.
    //
    // 5 x 8192 KB leaves room for two pickers in one flow (10 photos, 81920 KB) inside both
    // ceilings. A third would exceed the count, which validateFlow reports as an error.
    var maxPhotos = parseInt(properties['max-uploaded-photos'], 10);
    result['max-uploaded-photos'] = isNaN(maxPhotos) ? 5 : Math.min(maxPhotos, 10);

    var maxSizeKb = parseInt(properties['max-file-size-kb'], 10);
    result['max-file-size-kb'] = isNaN(maxSizeKb) ? 8192 : maxSizeKb;

    if (properties.enabled === false) {
      result.enabled = false;
    }

    if (properties.visible === false) {
      result.visible = false;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    return {
      name: whatsappComponent.name,
      label: whatsappComponent.label,
      'photo-source': whatsappComponent['photo-source'] || 'camera_gallery',
      'max-file-size': whatsappComponent['max-file-size'] || '',
      'min-uploaded-photos': whatsappComponent['min-uploaded-photos'] || '',
      'max-uploaded-photos': whatsappComponent['max-uploaded-photos'] || '',
      required: whatsappComponent.required || false,
      enabled: whatsappComponent.enabled !== false,
      visible: whatsappComponent.visible !== false,
      'helper-text': whatsappComponent['helper-text'] || '',
      'error-message': whatsappComponent['error-message'] || '',
    };
  },
};
